import { css } from '@emotion/react';
import styled from '@emotion/styled';
import React, { useCallback, useEffect } from 'react';

import { Alert, Box, Flex, Select } from '~/components/blocks';
import { MedixsPatientEventFragment } from '~/graphql/generated/graphql';
import { toMedixsWarekiDate } from '~/utils/date';

import { useFetchMedixsEvents } from './use-fetch-medixs-events';

type Props = {
  value?: number | null;
  medixsPatientId: string;
  onSelect: (medixsEvent: MedixsPatientEventFragment | null | undefined) => void;
};

const Root = styled(Flex)(({ theme }) =>
  css({
    borderRadius: theme.radii.default,
    alignItems: 'center',
  }),
);

const SelectBox = styled(Box)(() =>
  css({
    width: '300px',
  }),
);

export const SelectMedixsEvent = (props: Props) => {
  const { value, medixsPatientId, onSelect } = props;
  const { loading, medixsEvents } = useFetchMedixsEvents({ medixsPatientId });

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const selectedValue = Number(e.target.value);
      const event =
        selectedValue === -1 ? null : medixsEvents.find((e) => e.eventId === selectedValue);

      onSelect(event as MedixsPatientEventFragment | null | undefined);
    },
    [medixsEvents, onSelect],
  );

  useEffect(() => {
    if (medixsEvents && medixsEvents.length > 0 && value === undefined) {
      onSelect(medixsEvents[0] as MedixsPatientEventFragment);
      return;
    }

    if (medixsEvents && medixsEvents.length > 0 && !!value) {
      const selectedEvent = medixsEvents.find((e) => e.eventId === value);
      onSelect(selectedEvent as MedixsPatientEventFragment | null | undefined);
    }
  }, [medixsEvents, onSelect, value]);

  if (medixsEvents.length === 0 && !loading) {
    return (
      <Box width="100%">
        <Alert status="info">
          登録されている処方日がないため、今回のフォローアップ内容は薬歴に反映されません
        </Alert>
      </Box>
    );
  }

  return (
    <Root>
      <SelectBox>
        <Select value={value === null ? -1 : value} onChange={handleChange} fill>
          {medixsEvents?.map((event, idx) => (
            <option key={idx} value={event.eventId}>
              {toMedixsWarekiDate(event.eventDt)} {event.eventTitle}
            </option>
          ))}
          <option value={-1}>処方日指定なし（薬歴に反映しません）</option>
        </Select>
      </SelectBox>
    </Root>
  );
};
